import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

export const ExpenseScopeSchema = z.enum(["topic", "thread", "message"]);
export type ExpenseScope = z.infer<typeof ExpenseScopeSchema>;

export const ExpenseChannelFamilySchema = z.enum([
  "sms",
  "mms",
  "voice",
  "other",
]);
export type ExpenseChannelFamily = z.infer<typeof ExpenseChannelFamilySchema>;

export const ExpenseDirectionSchema = z.enum([
  "inbound",
  "outbound",
  "unknown",
]);
export type ExpenseDirection = z.infer<typeof ExpenseDirectionSchema>;

const metricBucketSchema = <T extends z.ZodTypeAny>(keySchema: T) =>
  z.object({
    key: keySchema,
    total: z.number(),
    average: z.number(),
    count: z.number().int().min(0),
  });

const expenseRowSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  teamId: z.string(),
  topicId: z.string().nullable(),
  threadId: z.string().nullable(),
  messageId: z.string().nullable(),
  expense: z.number(),
  currency: z.string(),
  type: z.string(),
  scope: ExpenseScopeSchema,
  channelFamily: ExpenseChannelFamilySchema,
  direction: ExpenseDirectionSchema,
  description: z.string().nullable(),
  createdAt: z.string(),
});

export const getSupportConversationExpenses = unaryRpc({
  name: "getSupportConversationExpenses" as const,
  input: z.object({
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    teamId: z.string().optional(),
    teamIds: z.array(z.string()).optional(),
    currency: z.string().optional(),
    types: z.array(z.string()).optional(),
    scopes: z.array(ExpenseScopeSchema).optional(),
    channelFamilies: z.array(ExpenseChannelFamilySchema).optional(),
    directions: z.array(ExpenseDirectionSchema).optional(),
    limit: z.number().int().min(1).max(200).optional(),
  }),
  output: z.object({
    currency: z.string(),
    totalSpend: z.number(),
    expenseCount: z.number().int().min(0),
    averagePerExpense: z.number(),
    availableCurrencies: z.array(z.string()),
    daily: z.array(
      z.object({
        date: z.string(),
        total: z.number(),
        count: z.number().int().min(0),
        byType: z.record(z.string(), z.number()),
        byScope: z.record(z.string(), z.number()),
      }),
    ),
    byType: z.array(metricBucketSchema(z.string())),
    byScope: z.array(metricBucketSchema(ExpenseScopeSchema)),
    byChannelFamily: z.array(metricBucketSchema(ExpenseChannelFamilySchema)),
    byDirection: z.array(metricBucketSchema(ExpenseDirectionSchema)),
    recentExpenses: z.array(expenseRowSchema),
  }),
});

export type GetSupportConversationExpensesInput = z.infer<
  typeof getSupportConversationExpenses.input
>;

export type GetSupportConversationExpensesOutput = z.infer<
  typeof getSupportConversationExpenses.output
>;
